import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChangeEvent, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { customerKeys } from 'api/queryKeys/queryKeys';
import { CustomerSearchRequest } from 'api/request/CustomerSearchRequest';
import { signOut } from 'api/sessionApi';
import { getCustomerList } from 'api/userApi';
import { Button } from 'components/widget/Button';
import { searchInfoState, updatedCustomer, userInfoState } from 'globalState/user/atom';
import { useRecoilState, useResetRecoilState } from 'recoil';
import { YN_TYPE } from 'types/enumType';
import { isAdminRole } from 'util/commonUtil';
import { decryptString } from 'util/encryptUtil';
import { convertRoleToString } from 'util/stringUtil';

export default function Header() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [userInfo, setUserInfo] = useRecoilState(userInfoState);
  const resetUserInfo = useResetRecoilState(userInfoState);
  const resetSearchInfo = useResetRecoilState(searchInfoState);
  const searchRequest = new CustomerSearchRequest(1, 9999);

  const handleChangeInfo = () => {
    navigate('/mypage');
  };

  const handleSignOut = async () => {
    await signOut();
    resetUserInfo();
    queryClient.clear();
    sessionStorage.clear();
  };

  const {
    data: customerData,
    refetch: refetchCustomer,
    isLoading: isLoadingCustomer,
  } = useQuery({
    queryKey: customerKeys.all,
    queryFn: () => getCustomerList({ ...searchRequest, activate: YN_TYPE.Y }),
    enabled: isAdminRole(decryptString(userInfo.role)),
  });

  const onChangeCustomer = (event: ChangeEvent<HTMLSelectElement>) => {
    const { value } = event.target;
    const customer = (customerData?.list ?? []).find(
      (customer) => customer.customerId === Number(value ?? -1),
    );
    if (!customer) {
      alert('고객사가 없습니다.');
      window.location.reload();
      return;
    }

    setUserInfo((prev) => ({
      ...prev,
      customerId: value,
      customerName: customer.name,
      insolationYn: customer.insolationYn,
    }));

    resetSearchInfo();
  };

  const [isUpdatedCustomer, setIsUpdatedCustomer] = useRecoilState(updatedCustomer);
  useEffect(() => {
    if (!isUpdatedCustomer.isUpdateCustomer) {
      return;
    }

    refetchCustomer();
    setIsUpdatedCustomer({ isUpdateCustomer: false });
  }, [isUpdatedCustomer]);

  useEffect(() => {
    if (!customerData) {
      return;
    }

    const customerList = customerData?.list ?? [];
    const targetCustomer = customerList.find(
      (customer) => customer.customerId === Number(userInfo?.customerId ?? -1),
    );

    if (customerList && customerList?.length > 0) {
      const { customerId, name, insolationYn } = targetCustomer ?? customerList[0];
      setUserInfo((prev) => ({
        ...prev,
        customerId: customerId.toString(),
        customerName: name,
        insolationYn,
      }));
    }
  }, [customerData]);

  return (
    <header className={'header'}>
      <h1 className={'logo-title'}>
        <i className={'logo'} />
        <div className={'select-wrap'}>
          {isAdminRole(decryptString(userInfo.role)) ? (
            <select
              className={'input-box-type-01'}
              value={userInfo.customerId}
              onChange={onChangeCustomer}
            >
              {!isLoadingCustomer &&
                customerData?.list?.map((customer) => (
                  <option value={customer.customerId} key={customer.customerId}>
                    {customer.name}
                  </option>
                ))}
            </select>
          ) : (
            <p>{userInfo.customerName}</p>
          )}
        </div>
      </h1>
      <section className={'header-right-section'}>
        <div className={'header-info'}>
          <label
            htmlFor={'user-name'}
          >{`${convertRoleToString(decryptString(userInfo.role))}: `}</label>
          <span className={'user-name'} id={'user-name'}>
            {userInfo.name}
          </span>
          <div className={'btn-group no-bg'}>
            <button type={'button'} className={'p-right btn-type-01'} onClick={handleChangeInfo}>
              {'정보변경'}
            </button>
            <Button name={'로그아웃'} className={'p-right btn-type-02'} onClick={handleSignOut} />
          </div>
        </div>
      </section>
    </header>
  );
}
